import axios from 'axios';
import { convertPersianToEnglishNumbers } from '../utils/validation';
import { apiPoolManager } from './apiPool';

interface SubscriptionData {
  plan: 'intern' | 'expert' | 'master';
  duration: 'quarterly' | 'semiannual' | 'annual';
  subscription_type: 'seller' | 'assistant';
  amount: number;
  start_date: string;
  expire_date: string;
  days_remaining: number;
}

interface SubscriptionResponse {
  status: 'success';
  has_active_subscription: boolean;
  message?: string;
  data?: SubscriptionData;
}

interface PersonalInfoResponse {
  full_name: string;
  phone_number: string;
  position: string;
  email: string;
}

interface BusinessInfoResponse {
  business_name: string;
  business_type: string;
  business_size: string;
  business_description: string;
  business_address: string;
  description?: string;
}

interface ServiceInfoResponse {
  service_type: string;
  service_description: string;
  target_audience: string;
}

export interface ChannelInfo {
  telegram_enabled: boolean;
  telegram_bot_token: string | null;
  website_chatbox_enabled: boolean;
  website_chatbox_id: string | null;
  website_address: string | null;
  bale_enabled: boolean;
  bale_bot_token: string | null;
  kavenegar_enabled: boolean;
  kavenegar_api_key: string | null;
  instagram_enabled: boolean;
  instagram_username: string | null;
  instagram_password: string | null;
}
export interface ChannelInfo_post {
  telegram_enabled: boolean;
  telegram_bot_token: string | null;
  website_chatbox_enabled: boolean;
  website_address: string | null;
  bale_enabled: boolean;
  bale_bot_token: string | null;
  kavenegar_enabled: boolean;
  kavenegar_api_key: string | null;
  instagram_enabled: boolean;
  instagram_username: string | null;
  instagram_password: string | null;
}

export interface InstagramStatus {
  enabled: boolean;
  connected: boolean;
  username: string | null;
  user_id: string | null;
  token_expires_at: string | null;
  token_valid: boolean;
}

export interface InstagramAuthorizeResponse {
  status: string;
  authorization_url: string;
  message: string;
}

export interface InstagramDisconnectResponse {
  status: string;
  message: string;
}

export interface OrderBot {
  where: string;
  type: string;
  input: string;
  message: string;
}

export interface AgentBehavior {
  conversion_style: 'PROACTIVE' | 'REACTIVE' | null;
  sales_role: 'REPRESENTATIVE' | 'ASSISTANT' | null;
  sales_methodology: 'CONSULTATIVE' | 'TRANSACTIONAL' | 'SOLUTION_BASED' | 'CHALLENGER' | 'SPIN' | 'SANDLER' | null;
  answer_length: 'LONG' | 'MEDIUM' | 'SHORT' | null;
}

export interface BehaviorFilterItem {
  name: string;
  label: string;
  description: string;
  response_type: string;
}

export interface BehaviorFilterData {
  [key: string]: boolean;
}

export interface BehaviorFilter {
  filters: BehaviorFilterData;
}

export interface Subtask {
  id: string;
  description: string;
  reasoning: string;
  success_criteria: string;
  locked: boolean;
}

export interface TwoFAForm {
  phone_number: string;
  two_factor_auth_code: string;
}

export const api = axios.create({
  headers: {
    'Content-Type': 'application/json',
  },

  timeout: 35000,
});

// Add token + dynamic api base url
api.interceptors.request.use(async (config) => {
  config.baseURL = await apiPoolManager.getActiveApi();

  const token = localStorage.getItem('token');

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  return config;
});

// Handle response errors + random failover
api.interceptors.response.use(
  (response) => response,

  async (error) => {
    const originalRequest = error.config;

    if (!originalRequest) {
      return Promise.reject(error);
    }

    // APIs already tested for this request
    if (!originalRequest._triedApis) {
      originalRequest._triedApis = [];
    }

    const currentApi =
      originalRequest.baseURL ||
      (await apiPoolManager.getActiveApi());

    // Save tested api
    if (
      currentApi &&
      !originalRequest._triedApis.includes(
        currentApi
      )
    ) {
      originalRequest._triedApis.push(
        currentApi
      );
    }

    const shouldSwitchApi =
      apiPoolManager.handleRequestError(error, currentApi);

    // Non-core errors should not switch api
    if (!shouldSwitchApi) {
      return Promise.reject(error);
    }

    // Pick random next api
    const nextApi =
      await apiPoolManager.getNextApi(
        originalRequest._triedApis
      );

    // All APIs tested
    if (!nextApi) {
      return Promise.reject(error);
    }

    try {
      originalRequest.baseURL = nextApi;

      return await api(originalRequest);
    } catch (retryError) {
      return Promise.reject(retryError);
    }
  }
);


// Auth APIs
export const auth = {
  sendSignupOTP: (phoneNumber: string, languagePreference: string = 'fa', utm_source: string, utm_medium: string, utm_campaign: string, utm_term: string, utm_content: string = '') => api.post('/auth/signup/phone', { phone_number: convertPersianToEnglishNumbers(phoneNumber), language_preference: languagePreference, utm_source: utm_source, utm_medium: utm_medium, utm_campaign: utm_campaign, utm_term: utm_term, utm_content: utm_content }),
  verifySignupOTP: (phoneNumber: string, otpCode: string, languagePreference: string = 'fa') => api.post('/auth/verify/phone', { phone_number: convertPersianToEnglishNumbers(phoneNumber), otp_code: convertPersianToEnglishNumbers(otpCode), language_preference: languagePreference }),
  sendSigninOTP: (phoneNumber: string) => api.post('/auth/signin/phone', null, { params: { phone_number: convertPersianToEnglishNumbers(phoneNumber) } }),
  verifySigninOTP: (phoneNumber: string, otpCode: string) => api.post('/auth/verify/signin', null, { params: { phone_number: convertPersianToEnglishNumbers(phoneNumber), otp_code: convertPersianToEnglishNumbers(otpCode) } }),


  sendSignupEmailOTP: (email: string, languagePreference: string = 'fa', utm_source: string, utm_medium: string, utm_campaign: string, utm_term: string, utm_content: string = '') => api.post('/auth/signup/email', { email: convertPersianToEnglishNumbers(email), language_preference: languagePreference, utm_source: utm_source, utm_medium: utm_medium, utm_campaign: utm_campaign, utm_term: utm_term, utm_content: utm_content }),
  verifySignupEmailOTP: (email: string, otpCode: string, languagePreference: string = 'fa') => api.post('/auth/verify/email', { email: convertPersianToEnglishNumbers(email), otp_code: convertPersianToEnglishNumbers(otpCode), language_preference: languagePreference }),
  sendSigninEmailOTP: (email: string) => api.post('/auth/signin/email', { email: convertPersianToEnglishNumbers(email) }),
  verifySigninEmailOTP: (email: string, otpCode: string) => api.post('/auth/verify/email', null , {params: { email: convertPersianToEnglishNumbers(email), otp_code: convertPersianToEnglishNumbers(otpCode) }}),
  
  verifySigninTwoFactor: (data: TwoFAForm) => api.post('/auth/signin/2fa', null, { params: data }),
  
  getCurrentUser: () => api.get('/auth/me'),
  updateUser: (data: { language_preference?: string }) => api.patch('/auth/me', data),


};

// Onboarding APIs
export const onboarding = {
  savePersonalInfo: (data: {
    full_name: string;
    phone_number: string;
    position: string;
    email: string;
  }) => {
    // Convert Persian numbers to English numbers in all fields
    const processedData = {
      full_name: data.full_name,
      phone_number: convertPersianToEnglishNumbers(data.phone_number),
      position: data.position  ,
      email: data.email,
    };
    
    return api.post('/onboarding/personal', processedData);
  },


  getPersonalInfo: () => api.get<PersonalInfoResponse>('/onboarding/personal'),


  saveBusinessInfo: (data: {
    business_name: string;
    business_type: string;
    business_size: string;
    business_description: string;
    business_address: string;
    description?: string;
  }) => api.post('/onboarding/business', data),


  getBusinessInfo: () => api.get<BusinessInfoResponse>('/onboarding/business'),


  saveServiceInfo: (data: {
    service_type: string;
    service_description: string;
    target_audience: string;
  }) => api.post('/onboarding/service', data),


  getServiceInfo: () => api.get<ServiceInfoResponse>('/onboarding/service'),


  saveChannelInfo: async (data: ChannelInfo_post) => {
    const response = await api.post('/onboarding/channel', data);
    return response.data;
  },


  getChannelInfo: () => api.get<ChannelInfo>('/onboarding/channel'),

  getWebsiteChatbox: () => api.get<{ website_chatbox_id: string | null }>('/onboarding/website_chatbox'),


  // Instagram Channel APIs
  getInstagramStatus: () => api.get<InstagramStatus>('/onboarding/channel_instagram/status'),
  authorizeInstagram: () => api.get<InstagramAuthorizeResponse>('/onboarding/channel_instagram/authorize'),
  disconnectInstagram: () => api.post<InstagramDisconnectResponse>('/onboarding/channel_instagram/disconnect'),

  initiateSubscription: (data: {
    plan: 'intern' | 'expert' | 'master';
    duration: 'QUARTERLY' | 'SEMIANNUAL' | 'ANNUAL';
    type: 'seller' | 'assistant';
    discount_code?: string | null;
  }) => {
    // Map 'type' to 'subscription_type' as expected by the API
    const apiParams = {
      plan: data.plan,
      duration: data.duration,
      subscription_type: data.type,
      ...(data.discount_code ? { discount_code: data.discount_code } : {}),
    };
    return api.post('/onboarding/subscription/initiate/', null, { params: apiParams });
  },

  
  verifySubscription: (status: string, authority: string) =>
    api.get('/onboarding/subscription/verify', {
      params: { Status: status, Authority: authority },
    }),

    
  getOnboardingStatus: () => api.get('/onboarding/status'),

  verifyCoupon: (coupon_code: string) => api.get('/onboarding/subscription/discount/validate', { params: { discount_code: coupon_code } }),

  getInitialInfo: () => api.get<{
    full_name: string | null;
    business_name: string | null;
    business_description: string | null;
  } | {
    data: {
      full_name: string | null;
      business_name: string | null;
      business_description: string | null;
    };
  }>('/onboarding/initial'),

  saveInitialInfo: (data: {
    full_name: string;
    business_name: string;
    business_description: string;
  }) => api.post('/onboarding/initial', data),

  initialSeller: (data: {
    plan: 'master' | 'expert' | 'intern';
    duration: 'QUARTERLY' | 'SEMIANNUAL' | 'ANNUAL';
    discount_code: string | null;
  }) => api.post('/onboarding/subscription/initiate-seller-v2', null, { params: data }),
};

// Payment APIs
export const payments = {
  getPaymentHistory: (params: {
    start_date?: string;
    end_date?: string;
    skip?: number;
    limit?: number;
  }) => api.get('/payments/history', { params }),
};

// Conversation APIs
export const conversations = {
  getChannels: () => api.get('/conversation/channels'),
  getChannelUsers: (channelName: string) => api.get(`/conversation/channel/${channelName}/users`),
  getThreadMessages: (channelName: string, username: string, params?: {
    skip?: number;
    limit?: number;
  }) => api.get(`/conversation/channel/${channelName}/user/${username}/messages`, { params }),
};

export const chatbox = {
  sendMessage: (
    chatboxId: string,
    body: { message: string; thread_id: string; username: string }
  ) => api.post(`/conversation/chatbox/${chatboxId}/message`, body),
  getResult: (correlationId: string) =>
    api.get(`/conversation/chatbox/result/${correlationId}`),
};

// Legacy Conversions APIs (kept for backward compatibility)
export const conversions = {
  getConversions: (params: {
    username?: string;
    start_date?: string;
    end_date?: string;
    skip?: number;
    limit?: number;
  }) => api.get('/conversation/list', { params }),
};

export const getSubscriptionInfo = async (): Promise<SubscriptionResponse> => {
  const response = await api.get('/onboarding/subscription/info');
  return response.data;
};

// Dashboard Types
export interface DashboardUser {
  id: number;
  phone_number: string;
  email: string | null;
  full_name: string | null;
  language_preference: string;
  is_active: boolean;
  is_verified: boolean;
  created_at: string;
  updated_at: string;
}

export interface DashboardAuth {
  provider: string;
  phone_verified: boolean;
}

export interface DashboardOnboarding {
  website_chatbox_id: string;
  telegram_enabled: boolean;
  website_chatbox_enabled: boolean;
  bale_enabled: boolean;
  kavenegar_enabled: boolean;
  instagram_enabled: boolean;
}

export interface ConversationByChannel {
  channel_name: string;
  message_count: number;
}

export interface TokenUsage {
  total_input_tokens: number;
  total_output_tokens: number;
  total_tokens: number;
}

export interface RecentPayment {
  amount: number;
  plan: string;
  ref_id: string;
}

export interface DashboardData {
  user: DashboardUser;
  auth: DashboardAuth;
  onboarding: DashboardOnboarding;
  knowledge_count: number;
  conversations_by_channel: ConversationByChannel[];
  conversations_last_7_days_by_channel: ConversationByChannel[];
  token_usage: TokenUsage;
  recent_payments: RecentPayment[];
  active_integrations: number;
  system_prompt?: string[];
}

// Dashboard API
export const dashboard = {
  getDashboardData: () => api.get<DashboardData>('/dashboard'),
};

// Behavior APIs
export const behavior = {
  getBehavior: () => api.get<AgentBehavior>('/behaviors'),
  updateBehavior: (data: AgentBehavior) => api.post<AgentBehavior>('/behaviors', data),
  getAvailableFilters: () => api.get<BehaviorFilterItem[]>('/behavior-filters/available'),
  getActiveFilters: () => api.get<{ [key: string]: boolean }>('/behavior-filters/active'),
  updateBehaviorFilter: (data: BehaviorFilter) => api.post('/behavior-filters/update', data),
  getSubtasks: () => api.get<Subtask[]>('/agent/subtasks'),
  toggleSubtaskLock: (subtaskId: string, locked: boolean) => api.patch(`/agent/subtasks/${subtaskId}/lock`, { 
    subtask_name: subtaskId,
    locked: locked 
  }),
  deleteSubtask: (subtaskId: string) => api.delete(`/agent/subtasks/${subtaskId}`,{
    params: {
      subtask_name: subtaskId,
    },
  }),
  updateSubtask: (subtaskId: string, data: {
    subtask_name: string;
    description: string;
    reasoning: string;
    success_criteria: string;
  }) => api.put(`/agent/subtasks/${subtaskId}`, data),
  
  addSubtask: (data: {
    description: string;
    reasoning: string;
    success_criteria: string;
    locked: boolean;
  }) => api.post('/agent/subtasks', data),
  getSystemPromptContent: () => api.get<{ system_prompt: string[]; system_prompt_locked: boolean }>('/agent/subtasks/system-prompt-content'),
  toggleSystemPromptLock: (locked: boolean) => api.patch('/agent/subtasks/lock/system-prompt-content', { 
    locked: locked 
  }),
  updateSystemPromptContent: (systemPrompt: string) => api.put('/agent/subtasks/system-prompt', { 
    system_prompt: systemPrompt 
  }),
};

export default api;

// Marketplace API Types
export interface ThirdPartyIntegrationList {
  id: number;
  title: string;
  brief_description: string;
  image_url?: string;
  monthly_price: string;
  is_active: boolean;
}

export interface ThirdPartyIntegrationDetail {
  id: number;
  title: string;
  brief_description: string;
  detailed_description?: string;
  image_url?: string;
  third_party_link?: string;
  configuration_schema: any;
  monthly_price: string;
  terms_and_conditions?: string;
  is_active: boolean;
  created_at: string;
  updated_at?: string;
}

export interface UserIntegrationWithDetails {
  id: number;
  user_id: number;
  configuration: any;
  is_active: boolean;
  notes?: string;
  created_at: string;
  updated_at?: string;
  integration: ThirdPartyIntegrationDetail;
}

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  size: number;
  total_pages: number;
}

// Marketplace APIs
export const marketplace = {
  // Get all available integrations
  getIntegrations: (params?: {
    page?: number;
    size?: number;
    title_filter?: string;
  }) => api.get<PaginatedResponse<ThirdPartyIntegrationList>>('/marketplace/integrations', { params }),

  // Get integration details
  getIntegrationDetail: (integrationId: number) => 
    api.get<ThirdPartyIntegrationDetail>(`/marketplace/integrations/${integrationId}`),

  // Add integration to user's AIDA
  addIntegration: (integrationId: number) =>
    api.post(`/marketplace/integrations/${integrationId}/add`),

  // Get user's integrations
  getUserIntegrations: (params?: {
    page?: number;
    size?: number;
    is_active?: boolean;
  }) => api.get<PaginatedResponse<UserIntegrationWithDetails>>('/marketplace/my-integrations', { params }),

  // Get user integration details
  getUserIntegrationDetail: (userIntegrationId: number) =>
    api.get<UserIntegrationWithDetails>(`/marketplace/my-integrations/${userIntegrationId}`),

  // Setup/configure integration
  setupIntegration: (userIntegrationId: number, data: {
    configuration: any;
    notes?: string;
  }) => api.put(`/marketplace/my-integrations/${userIntegrationId}/setup`, data),

  // Delete integration
  deleteIntegration: (userIntegrationId: number) =>
    api.delete(`/marketplace/my-integrations/${userIntegrationId}`),
};

// Membership API Types
export interface MembershipMember {
  id: number;
  member_user_id: number;
  owner_user_id: number;
  group_id: number;
  status: string;
  full_name: string | null;
  phone_number: string;
  email: string | null;
  group_name: string;
  created_at: string;
  updated_at: string | null;
}

export type InvitationStatus = 'pending' | 'accepted' | 'revoked';

export interface MembershipInvitation {
  id: number;
  owner_user_id: number;
  group_id: number;
  phone_number: string;
  email: string;
  status: InvitationStatus;
  created_at: string;
  updated_at: string | null;
}

export interface MembershipGroup {
  id: number;
  name: string;
  description: string;
  is_template: boolean;
  owner_user_id: number | null;
  api_permissions: string[];
  ui_modules: string[];
  created_at: string;
  updated_at: string | null;
}

export interface CreateInvitationPayload {
  group_id: number;
  phone_number: string;
  email: string;
}

export interface MembershipLimit {
  limit: number;
  used: number;
  remaining: number;
}

export interface MembershipMeResponse {
  is_member: boolean;
  ui_modules: string[];
}

// Membership APIs
export interface UpdateMemberGroupPayload {
  group_id: number;
}

export const membership = {
  getMe: () => api.get<MembershipMeResponse>('/membership/me'),
  getMembers: () => api.get<MembershipMember[]>('/membership/members'),
  deleteMember: (id: number) => api.delete(`/membership/members/${id}`),
  updateMemberGroup: (membershipId: number, data: UpdateMemberGroupPayload) =>
    api.patch<MembershipMember>(`/membership/members/${membershipId}/group`, data),
  getLimit: () => api.get<MembershipLimit>('/membership/limit'),
  getGroups: () => api.get<MembershipGroup[]>('/membership/groups'),
  getInvitations: () => api.get<MembershipInvitation[]>('/membership/invitations'),
  createInvitation: (data: CreateInvitationPayload) =>
    api.post<MembershipInvitation>('/membership/invitations', data),
  deleteInvitation: (invitationId: number) => api.delete(`/membership/invitations/${invitationId}`),
};

// RAG Documents API Types
export type DocStatus = string;

export interface DocStatusResponse {
  id: string;
  content_summary: string;
  content_length: number;
  status: DocStatus;
  created_at: string;
  updated_at: string;
  track_id?: string;
  chunks_count?: number;
  error_msg?: string;
  metadata?: Record<string, any>;
  file_path: string;
}

export interface DocsStatusesResponse {
  statuses: Record<DocStatus, DocStatusResponse[]>;
}

// RAG Documents APIs
export const rag = {
  getDocuments: () => api.get<DocsStatusesResponse>('/rag/documents'),
}; 